import React, { useState, ChangeEvent, KeyboardEvent, useEffect } from "react";
import { CircularProgress, TextField } from "@mui/material";
import { AddressData } from "../../../types/address";
import styles from './CepField.module.css';

interface CepFieldProps {
  value?: string; // Valor inicial do campo CEP, opcional
  onCepChange: (addressData: AddressData) => void; // Callback para atualizar os dados de endereço no componente pai
  customClassName?: string;
  error?: boolean;
  helperText?: React.ReactNode;
}

const CepField: React.FC<CepFieldProps> = ({
  value = "",
  onCepChange,
  customClassName,
  error,
  helperText,
}) => {
  const [cep, setCep] = useState<string>(value); // Inicializa com o valor informado ou vazio
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setCep(value);
  }, [value]);

  const handleCEPChange = async (cep: string) => {
    cep = cep.replace(/\D/g, ""); // Remove caracteres não numéricos
    if (cep.length === 8) {
      setLoading(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();

        const newAddressData: AddressData = {
          cep: data.cep,
          logradouro: data.logradouro,
          bairro: data.bairro,
          localidade: data.localidade,
          uf: data.uf,
        };

        if (!data.erro) {
          onCepChange(newAddressData); // Atualiza os dados de endereço com sucesso
        } else {
          onCepChange({
            cep: "",
            logradouro: "",
            bairro: "",
            localidade: "",
            uf: "",
          } as AddressData);
          alert("CEP não encontrado");
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
        alert("Erro ao buscar CEP, tente novamente mais tarde");
      } finally {
        setLoading(false);
      }
    }
  };

  const formatarCEP = (cep: string): string => {
    const numeros = cep.replace(/\D/g, "").slice(0, 8);

    if (numeros.length >= 6) {
      // Formato: XX.XXX-XXX
      return numeros.replace(/(\d{2})(\d{3})(\d{1,3})/, "$1.$2-$3");
    } else if (numeros.length >= 3) {
      // Formato: XX.XXX
      return numeros.replace(/(\d{2})(\d{1,3})/, "$1.$2");
    } else {
      return numeros;
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setCep(inputValue); // Atualiza o estado local do CEP
  };

  return (
    <div className={styles.container}>
      <TextField
        label=""
        size="small"
        data-testid="cep-input"
        required
        fullWidth={!loading ? true : false}
        value={formatarCEP(cep)} // Formata e exibe o valor do CEP
        onChange={handleInputChange} // Permite novos valores
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleCEPChange(cep); // Faz a busca ao pressionar Enter
          }
        }}
        disabled={loading}
        onBlur={() => handleCEPChange(cep)} // Faz a busca ao perder o foco
        variant="standard"
        color="secondary"
        error={error}
        helperText={helperText}
        InputLabelProps={{
          shrink: false,
        }}
        InputProps={{
          className: customClassName,
          disableUnderline: true,
        }}
      />
      {loading && <CircularProgress style={{marginLeft:5}} />}
    </div>
  );
};

export default CepField;
